import {
  APICallState,
  LoginInfo,
  Selectors,
  useIsDesktop,
  useWidgetState,
} from '@eCommerce/frontend-legacy-shared-library-native';
import {useDispatch, useSelector} from 'Provider';
import React, {useCallback, useEffect} from 'react';

import GatedPersonalInfoModule from 'components/GatedProfilePanels/GatedPersonalInfoModule';
import PersonalInfoModule from 'components/PersonalInfoModule';
import MFAGate from 'containers/MFAGate';
import {useMFAStepUp} from 'hooks/useMFAStepUp';
import {useMaskPhoneNumber} from 'hooks/useMaskPhoneNumber';
import {useMaskZip} from 'hooks/useMaskZip';
import {AdobeEvent, useTrackEvent} from 'hooks/useTrackEvent';
import {profileTabForwardUrl} from 'models/MFA';
import {ProfileActions, profileInfoStateSelector} from 'store/profile';

const ConnectedPersonalInfoModule: React.FC = () => {
  const dispatch = useDispatch();
  const isDesktop = useIsDesktop();
  const {maskNumber} = useMaskPhoneNumber();
  const {onEdit, stopEditing, isPanelUnlocked, isEditing, isMFAStepUpLoading} =
    useMFAStepUp({
      forwardUrl: profileTabForwardUrl,
    });
  const profileInfoState = useSelector(profileInfoStateSelector);
  const logininfo = useWidgetState(Selectors.loginInfoSelector) as LoginInfo;
  const getGuestProfileCallState = useWidgetState(
    Selectors.getGuestProfileCallStateSelector
  ) as APICallState;
  const address = logininfo.addresses?.address.find(a => a.location === 'Home');
  const homephone = logininfo.phonenumbers?.find(n => n.location === 'Home')
    ?.phonenumber;
  const cellphone = logininfo.mobilenumber?.number;
  const country = address?.country?.toUpperCase();
  const maskZip = useMaskZip({
    isCanada: country === 'CAN',
    isUSA: country === 'USA',
  });
  const zip =
    address?.zipcode === '000000' ? undefined : maskZip(address?.zipcode);

  const handleSaveProfileInfo = useCallback(
    data => {
      dispatch(ProfileActions.updateProfileInfo(data));
    },
    [dispatch]
  );
  const {trackAdobeEvent} = useTrackEvent();

  const onOverlayAnimationFinish = () => {
    if (profileInfoState === APICallState.SUCCESS) {
      trackAdobeEvent({
        event: AdobeEvent.click,
        click: {
          name: 'personal information edited',
        },
      });
      stopEditing();
    }
    dispatch(ProfileActions.resetProfileInfoState());
  };

  useEffect(() => {
    stopEditing();
  }, [stopEditing]);

  return (
    <MFAGate
      forwardUrl={profileTabForwardUrl}
      GatedComponent={GatedPersonalInfoModule}
      forceLoading={getGuestProfileCallState !== APICallState.SUCCESS}>
      <PersonalInfoModule
        isEditLoading={isMFAStepUpLoading && isEditing}
        email={logininfo?.email}
        homePhone={maskNumber(homephone)}
        cellPhone={maskNumber(cellphone)}
        country={address?.country}
        city={address?.city}
        address={address?.address1}
        address2={address?.address2}
        stateprovince={address?.stateprovince}
        zip={zip}
        handleSaveProfileInfo={handleSaveProfileInfo}
        profileInfoState={profileInfoState}
        onOverlayAnimationFinish={onOverlayAnimationFinish}
        onEdit={onEdit}
        stopEditing={stopEditing}
        isPanelUnlocked={isPanelUnlocked && isEditing}
      />
    </MFAGate>
  );
};

export default ConnectedPersonalInfoModule;
